// vue-3-common dependencies

import { mergeOptionsWithAuth } from '@agyletime/vue-3-common/src/utils/apiService';
import { useEnvironmentData } from '@agyletime/vue-3-common/src/composables/environmentData';
// composables
import { extractUniqueLocationsFromUsers } from '../composables/extractUniqueLocationsFromUsers';
import { getShiftTemplatesAndExtractData } from '../composables/getShiftTemplatesAndExtractData';
import { extractBusinessRolesFromUsers } from '../composables/extractBusinessRolesFromUsers';
import { getAllTasks } from '../composables/getAllTasksAndLeave';
import { getShiftLocations } from '../composables/getShiftLocations';

// typings
import { FilterData } from '../types/FilterData';
import {
    User, Location, BusinessRole
} from '../types/UsersFilterData';
import { TasksFilterData } from '../types/TasksFilterData';
import { Locations } from '../types/Locations';
import { Team } from '../types/Team';

import { getAllTeams, getMyTeam } from './teamService';

import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { useCurrentUser } from '../composables/useCurrentUser';
import { ShiftTemplate } from '../types/ShiftTemplate';

const logger = useLogger('Filter Component -> dependencies');
const environmentData = await useEnvironmentData();

// Helper function to fetch full response including pagination info
class HttpError extends Error {
    status: number;
    
    constructor(message: string, status: number) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
    }
}
// Type for paginated API responses
interface PaginatedResponse<T> {
    data?: T[];
    nextPageToken?: string;
}

const fetchFromLambda = (url: string, options?: { headers?: Record<string, string>; method?: string; body?: string }) => {
    const optionsWithAuth = mergeOptionsWithAuth(options);

    return fetch(`${environmentData.apiGatewayBaseUrl}${url}`, optionsWithAuth)
        .then((response) => {
            if (!response.ok) {
                throw new HttpError(response.statusText, response.status || 400);
            }
            if (response.status === 204) {
                return null;
            }
            return response.json();
        });
};

// Helper function to fetch all pages of users
async function fetchAllUserPages(baseUrl: string): Promise<User[]> {
    const allUsers: User[] = [];
    let nextPageToken: string | null = null;
    
    do {
        const url = nextPageToken ? `${baseUrl}&nextPageToken=${nextPageToken}` : baseUrl;
        const response = await fetchFromLambda(url) as User[] | PaginatedResponse<User>;
        const users = Array.isArray(response) ? response : response.data || [];
        allUsers.push(...users);
        nextPageToken = Array.isArray(response) ? null : (response.nextPageToken || null);
    } while (nextPageToken);
    
    return allUsers;
}

// Helper function to check if user is admin
function isAdmin(currentUser: { permissions?: string[] } | null): boolean {
    return currentUser?.permissions?.includes('can_view_all_teams') || false;
}

export async function getUsers(): Promise<User[]> {
    const { getCurrentUser } = useCurrentUser();
    const currentUser = getCurrentUser();
 
    if (isAdmin(currentUser)) {
        // Admin: Get all users with pagination
        return await fetchAllUserPages('/organisation/users?');
    }
    
    // Manager: Get users from managed teams only (match roster filtering) with pagination
    const managedTeams = await getTeams();
    const teamIds = managedTeams.map((team) => team.id);
    return await fetchAllUserPages(`/organisation/users?teamIds=${teamIds.join(',')}`);
}

// To fetch teams
export async function getTeams(): Promise<Team[]> {
    const { getCurrentUser } = useCurrentUser();
    const currentUser = getCurrentUser();

    // Match the exact permission check from manage-user-definitions.component.js
    if (currentUser?.permissions?.includes('can_view_all_teams')) {
        
        return await getAllTeams();
    } else {
        // Manager: Get only managed teams + own team (match manage roster pattern)
        const managedTeams = await getAllTeams(currentUser?.id);
        
        // Add user's own team if not already included (match the manage roster logic)
        const myTeam = await getMyTeam({ cache: true });
        if (myTeam && !managedTeams.find((t) => t.id === myTeam.id)) {
            managedTeams.push(myTeam);
        }
        
        return managedTeams.sort((a, b) => a.name.localeCompare(b.name));
    }
}

// To fetch users unique location
async function getUsersLocation(users: User[]): Promise<Location[]> {
    const userLocationsFromUsers = extractUniqueLocationsFromUsers(users as User[]);
    return userLocationsFromUsers;
}

// SHIFTS FILTER:
// To fetch shift Templates and shift locations
async function getShiftData(): Promise<{ shiftTemplates: ShiftTemplate[] }> {
    return await getShiftTemplatesAndExtractData();
}

// TASKS FILTER:
// To fetch all tasks along with leave type
async function getTasks(): Promise<TasksFilterData> {
    return await getAllTasks();
}

export async function loadDependencies(): Promise<{ filterData: FilterData }> {
    const results = await Promise.allSettled([
        getUsers(),
        getTeams(),
        getShiftData(),
        getShiftLocations(),
        getTasks()
    ]);

    const users = results[0].status === 'fulfilled' ? results[0].value : [];
    const allTeams = results[1].status === 'fulfilled' ? results[1].value : [];
    const businessRoles = extractBusinessRolesFromUsers(users as User[]);
    const shiftTemplateData = results[2].status === 'fulfilled' ? results[2].value : { shiftTemplates: [] };
    const shiftLocations: Locations[] = results[3].status === 'fulfilled' ? results[3].value : [];
    const taskData = results[4].status === 'fulfilled' ? results[4].value : { tasks: [], leaveTasks: [] } as TasksFilterData;
    const userLocationsFromUsers = await getUsersLocation(users as User[]);
    // Extract unique tags from users
    const userTags = users
        .flatMap((user) => user.tags || [])
        .map((tag) => tag.text)
        .filter((tagText, index, array) => array.indexOf(tagText) === index)
        .sort((a, b) => a.localeCompare(b));

    // Log any errors that occurred
    results.forEach((result, index) => {
        if (result.status === 'rejected') {
            const serviceNames = [
                'users', 'teams', 'businessRoles', 'shiftTemplates', 'shiftLocations', 'tasks'
            ];
            logger.error(`Failed to load ${serviceNames[index]}:`, result.reason);
        }
    });

    const filterData: FilterData = {
        teams: allTeams,
        users: users as User[],
        businessRoles: businessRoles as BusinessRole[],
        tasks: taskData.tasks,
        leaveTasks: taskData.leaveTasks,
        userLocations: userLocationsFromUsers,
        shiftTemplates: shiftTemplateData.shiftTemplates,
        shiftLocations: shiftLocations,
        tags: userTags
    };

    return { filterData };
}
